import {Box, Grid, Modal, Stack, Typography} from '@mui/material';
import {useEffect, useState, useRef, useCallback, useMemo} from 'react';

import ToolHeader from '../atomicComponents/ToolHeader';
import BaseMedia from './BaseMedia';
import BaseWidget from './BaseWidget';
import Tools from '../globalComponents/Tools';
import SimDataTable from '../globalComponents/SimDataTable';
import SimWidget from "../globalComponents/SimWidget";
import {usePlotAndValidateField} from "../../hooks/usePlotAndValidateField";
import {usePlotAndValidateFields} from "../../hooks/usePlotAndValidateFields";
import {useToolData} from "../../hooks/useToolData";
import {useResetDefaults} from "../../hooks/useResetDefaults";
import {useLoadingFetcher} from "../../hooks/useLoadingFetcher";
import {getRunMetaData, getSimulation, postSession} from "../../utils/api";
import {Dangerous, Warning} from "@mui/icons-material";
import SimulationMeta from "../globalComponents/SimulationMeta";

const hasErrors = (error) => {
  return error && Object.keys(error).length > 0;
};

const BaseTool = ({
                    id,
                    setId,
                    tool,
                    toolName,
                    sharedDeps={},
                    flagTriggers={},
                    flagDeps={},
                    flagValidators,
                    defaults,
                    sliceWidget: SliceComponent,
                    pointWidget: PointComponent, 
                    viewWidget: ViewComponent, 
                    obsWidget: ObsComponent
                  }) => {
  const [modelMetaData, setModelMetaData] = useState({})
  const [simulationOverlay, setSimulationOverlay] = useState(false)
  useEffect(() => {
    postSession(id)
      .then(response => {
        const returnId = response.data.key_id.toString()
        localStorage.setItem('id', returnId);
        setId(returnId)
        getSimulation(returnId)
          .then((response) => {
            const {data} = response;
            if (data && data.cor_id) {
              setSimulationOverlay(false)
            } else {
              setSimulationOverlay(true)
            }
          })
          .catch((error) => {
            if (error.response.status === 404) {
              setSimulationOverlay(true)
            }
          });
      })
      .catch(error => {

      });
  }, []);

  const [shared, setShared] = useState(sharedDeps || {})
  const [flag, setFlag] = useState(flagDeps || {})
  const [cacheTrigger, setCacheTrigger] = useState(Date.now());
  const flagDepsRef = useRef(flag);
  const {
    pushApiCount,
    popApiCount,
    isLoading,
    isThrottled
  } = useLoadingFetcher();

  const {
    toolData,
    parameters,
    modifications,
    warnings,
    errors,
    serverError,
    setParameters,
    handleErrorSetting,
    handleDataUpdating,
    multipleWidgetSetter
  } = useToolData(id, tool, setCacheTrigger, pushApiCount, popApiCount);

  useEffect(() => {
    flagDepsRef.current = flag;
  }, [flag]);

  const sharedData = useMemo(() => {
    return shared;
  }, [{...shared}]);

  const sharedDependencies = Object.keys(sharedDeps)
  .flatMap((topKey) =>
    Object.keys(sharedDeps[topKey]).flatMap((subKey) => {
      return parameters[topKey]?.[subKey]
    })
  );

  const flagDependencies = Object.keys(flagDeps)
  .flatMap((topKey) =>
    Object.keys(flagDeps[topKey]).flatMap((subKey) => {
      return parameters[topKey]?.[subKey]
    })
  );

  useEffect(() => {
    setShared((prevData) =>
      Object.keys(prevData).reduce((updated, widgetKey) => {
          updated[widgetKey] = {
            ...prevData[widgetKey],
            ...Object.keys(prevData[widgetKey]).reduce((acc, subKey) => {
              acc[subKey] = parameters[widgetKey][subKey];
              return acc;
            }, {}),
          };
        return updated;
      }, {})
    );
  }, sharedDependencies);

  useEffect(() => {
    setFlag((prevData) =>
      Object.keys(prevData).reduce((updated, widgetKey) => {
          updated[widgetKey] = {
            ...prevData[widgetKey],
            ...Object.keys(prevData[widgetKey]).reduce((acc, subKey) => {
              acc[subKey] = parameters[widgetKey][subKey];
              return acc;
            }, {}),
          };
        return updated;
      }, {})
    );
  }, flagDependencies);

  useEffect(() => {
    if (modelMetaData && (Object.keys(modelMetaData).length === 0) && parameters.sim_widget.cor_id) {
      getRunMetaData({cor_id: parameters.sim_widget.cor_id})
        .then((response) => {
          const {data} = response;
          setModelMetaData(data)
        })
        .catch((error) => {
          setModelMetaData({})
        });
    }
  }, [parameters.sim_widget.cor_id])

  const plotAndValidateField = usePlotAndValidateField({
    id,
    tool,
    flagTriggers,
    flagValidators,
    flagDepsRef,
    multipleWidgetSetter,
    handleDataUpdating,
    handleErrorSetting,
    pushApiCount,
  });

  const plotAndValidateFields = usePlotAndValidateFields({
    id,
    tool,
    handleDataUpdating,
    handleErrorSetting,
    pushApiCount,
  });

  const resetDefaults = useResetDefaults({
    defaults,
    parameters,
    modifications,
    plotAndValidateFields,
    multipleWidgetSetter
  });

  const configuredMedia = useMemo(() => {
    return (
      <BaseMedia
        meta={toolData}
        loading={isLoading}
        validationError={Object.values(errors).some(hasErrors)}
        cacheTrigger={cacheTrigger}
        data={parameters} mods={modifications}
        update={(k, v) => plotAndValidateField('plot_widget', k, v)}
        setter={(k, v) => setParameters((prevData) => ({...prevData, plot_widget: {...prevData.plot_widget, [k]: v},}))}
        tool={tool}
        resetDefaults={resetDefaults}
      />
    );
  }, [
    toolData,
    isLoading,
    errors,
    parameters,
    modifications,
    plotAndValidateField,
    tool,
  ]);

  const configuredSimTable = useMemo(() => {
    return (
      <SimDataTable
        id={id}
        data={parameters.sim_widget}
        update={(params) => plotAndValidateFields('sim_widget', params)}
        setter={(params) => setParameters((prevData) => ({...prevData, sim_widget: {...prevData.sim_widget, ...params},}))}
        modalViewSetter={setSimulationOverlay}
        modelMetaDataSetter={setModelMetaData}
      />
    );
  }, [
    parameters.sim_widget,
    plotAndValidateFields,
    setParameters,
    setSimulationOverlay
  ]);

  const configuredSimWidget = useMemo(() => {
    if (!parameters.sim_widget || Object.keys(parameters.sim_widget).length === 0) {
      return null;
    }
    return (
      <SimWidget
        data={parameters.sim_widget} mods={modifications.sim_widget} warn={warnings.sim_widget} errs={errors.sim_widget}
        update={(k, v) => plotAndValidateField('sim_widget', k, v)}
        setter={(k, v) => setParameters((prevData) => ({...prevData, sim_widget: {...prevData.sim_widget, [k]: v},}))}
        tool={tool}
      />
    );
  }, [
    parameters.sim_widget, 
    modifications.sim_widget, 
    warnings.sim_widget, 
    errors.sim_widget, 
    sharedData, 
    plotAndValidateField,
    tool
  ]);
  
  const configuredSliceWidget = useMemo(() => {
    if (!parameters.slice_widget || Object.keys(parameters.slice_widget).length === 0) {
      return null;
    }
    return (
      <SliceComponent
        data={parameters.slice_widget} mods={modifications.slice_widget} warn={warnings.slice_widget} errs={errors.slice_widget}
        update={(k, v) => plotAndValidateField('slice_widget', k, v)}
        setter={(k, v) => setParameters((prevData) => ({...prevData, slice_widget: {...prevData.slice_widget, [k]: v},}))}
        shared={sharedData}
      />
    );
  }, [
    parameters.slice_widget, 
    modifications.slice_widget, 
    warnings.slice_widget, 
    errors.slice_widget, 
    sharedData, 
    plotAndValidateField,
  ]);
  
  const configuredViewWidget = useMemo(() => {
    if (!parameters.view_widget || Object.keys(parameters.view_widget).length === 0) {
      return null;
    }
    return (
      <ViewComponent
        data={parameters.view_widget} mods={modifications.view_widget} warn={warnings.view_widget} errs={errors.view_widget}
        update={(k, v) => plotAndValidateField('view_widget', k, v)}
        setter={(k, v) => setParameters((prevData) => ({...prevData, view_widget: {...prevData.view_widget, [k]: v},}))}
        shared={sharedData}
      />
    );
  }, [
    parameters.view_widget, 
    modifications.view_widget, 
    warnings.view_widget, 
    errors.view_widget, 
    sharedData, 
    plotAndValidateField,
  ]);
  
  const configuredPointWidget = useMemo(() => {
    if (!parameters.point_widget || Object.keys(parameters.point_widget).length === 0) {
      return null;
    }
    return (
      <PointComponent
        data={parameters.point_widget} mods={modifications.point_widget} warn={warnings.point_widget} errs={errors.point_widget}
        update={(k, v) => plotAndValidateField('point_widget', k, v)}
        setter={(k, v) => setParameters((prevData) => ({...prevData, point_widget: {...prevData.point_widget, [k]: v},}))}
        shared={sharedData}
      />
    );
  }, [
    parameters.point_widget, 
    modifications.point_widget, 
    warnings.point_widget, 
    errors.point_widget, 
    sharedData, 
    plotAndValidateField,
  ]);

  const configuredObsWidget = useMemo(() => {
    if (!parameters.obs_widget || Object.keys(parameters.obs_widget).length === 0) {
      return null;
    }
    return (
      <ObsComponent
        data={parameters.obs_widget} mods={modifications.obs_widget} warn={warnings.obs_widget} errs={errors.obs_widget}
        update={(k, v) => plotAndValidateField('obs_widget', k, v)}
        setter={(k, v) => setParameters((prevData) => ({...prevData, obs_widget: {...prevData.obs_widget, [k]: v},}))}
        shared={sharedData}
      />
    );
  }, [
    parameters.obs_widget, 
    modifications.obs_widget, 
    warnings.obs_widget, 
    errors.obs_widget, 
    sharedData, 
    plotAndValidateField,
  ]);

return (
  <>
    {/* Simulation table modal (responsive sizing) */}
    <Modal
      open={simulationOverlay}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '90%',
          height: '90%',
          border: '2px solid #ccc',
          borderRadius: '8px',
          boxShadow: '5px 5px 5px rgba(0, 0, 0, 0.2)',
          bgcolor: 'white',
          p: { xs: 1, sm: 2 },
          position: 'relative',
          overflow: 'auto',
        }}
      >
        {configuredSimTable}
      </Box>
    </Modal>

    {/* Throttle modal */}
    <Modal
      open={isThrottled}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Stack alignItems="center" position="absolute" maxWidth="300px" textAlign="center">
        <Dangerous color="error" sx={{ fontSize: '80px' }} />
        <Typography color="white" fontSize={14}>GENERATING VISUALIZATIONS</Typography>
        <Typography color="white" fontSize={12} fontStyle="italic">
          Your visualization queue is full. Please wait for the plots to finish loading.
        </Typography>
      </Stack>
    </Modal>

    {/* Page layout */}
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
      <Grid
        container
        spacing={1}
        alignItems="flex-start"
        justifyContent="center"
      >
        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
          <Stack spacing={1} sx={{ width: '100%' }}>
            <ToolHeader tool={toolName} err={serverError} />
            <Box sx={{ width: '100%' }}>{configuredMedia}</Box>
            <SimulationMeta corId={parameters.sim_widget.cor_id} metaData={modelMetaData} />
          </Stack>
        </Grid>

        <Grid item xs={12} md={3} sx={{ order: { xs: 3, md: 1 } }}>
          <Stack spacing={1} sx={{ width: '100%' }}>
            {parameters.obs_widget && Object.keys(parameters.obs_widget).length > 0 && (
              <BaseWidget errs={hasErrors(errors.obs_widget)} widgetName="COMPARISON">
                {configuredObsWidget}
              </BaseWidget>
            )}
            {parameters.slice_widget && Object.keys(parameters.slice_widget).length > 0 && (
              <BaseWidget errs={hasErrors(errors.slice_widget)} widgetName="DATA">
                {configuredSliceWidget}
              </BaseWidget>
            )}
            {parameters.point_widget && Object.keys(parameters.point_widget).length > 0 && (
              <BaseWidget errs={hasErrors(errors.point_widget)} widgetName="POINT">
                {configuredPointWidget}
              </BaseWidget>
            )}
            {parameters.view_widget && Object.keys(parameters.view_widget).length > 0 && (
              <BaseWidget errs={hasErrors(errors.view_widget)} widgetName="VIEW">
                {configuredViewWidget}
              </BaseWidget>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={3} sx={{ order: { xs: 2, md: 3 } }}>
          <Stack spacing={1} sx={{ width: '100%' }}>
            <BaseWidget widgetName="TOOLS">
              <Tools modalValue={simulationOverlay} modalSetter={setSimulationOverlay} />
            </BaseWidget>
            {parameters.sim_widget && Object.keys(parameters.sim_widget).length > 0 && (
              <BaseWidget errs={hasErrors(errors.sim_widget)} widgetName="SIMULATION">
                {configuredSimWidget}
              </BaseWidget>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  </>
);

};

export default BaseTool;